import { kebabCase } from "lodash";
import Link from "next/link";
import { useState } from "react";
import { MoreHorizontal, Eye, EyeOff } from "react-feather";
import { ProtocolItem, Status } from "../../../types";
import { Badge } from "../Badge";
import { DropdownMenu } from "../DropdownMenu";
import { IconButton } from "../IconButton";
import { DeleteProtocolDialog } from "./DeleteProtocolDialog";
import { EditProtocolDialog } from "./EditProtocolDialog";
import { ManageClinicsDialog } from "../Course/ManageClinicsDialog";
import { useUpdateProtocolVisibility } from "../../../data/mutations/useUpdateProtocolVisibility";
import { toast } from "react-toastify";

type ProtocolCardProps = {
  protocol: ProtocolItem;
  canManage: boolean;
};

export const ProtocolCard = ({ protocol, canManage }: ProtocolCardProps) => {
  const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);
  const [isEditProtocolDialogOpen, setIsEditProtocolDialogOpen] = useState(false);
  const [isDeleteProtocolDialogOpen, setIsDeleteProtocolDialogOpen] = useState(false);
  const [isManageClinicsDialogOpen, setIsManageClinicsDialogOpen] = useState(false);

  const [updateProtocolVisibility] = useUpdateProtocolVisibility();

  const handleSaveProtocolVisibility = async (protocolId: string, clinicIds: string[]) => {
    try {
      await updateProtocolVisibility({
        variables: {
          protocolId,
          clinicIds,
        },
      });
    } catch (error) {
      toast("Failed to update protocol visibility", { type: "error" });
      throw error;
    }
  };

  const slug = `${kebabCase(protocol.name)}-${protocol.id.toString()}`;

  return (
    <>
      <Link href={`/protocols/${slug}`} key={protocol.id} passHref legacyBehavior>
        <a
          className={
            "flex cursor-pointer flex-col rounded-md border-[1px] border-gray-300 bg-gray-50 p-4 transition duration-standard ease-in-out hover:border-gray-400 hover:bg-white"
          }>
          <div className={"flex items-center justify-between"}>
            <div className={"flex items-center justify-center gap-3"}>
              <h2 className="leading-0 text-lg">{protocol.name}</h2>
              {protocol.status === Status.draft ? (
                <Badge>{protocol.status.toUpperCase()}</Badge>
              ) : null}
            </div>

            {canManage && (
              <DropdownMenu.DropdownMenu
                open={isDropdownMenuOpen}
                onOpenChange={setIsDropdownMenuOpen}>
                <DropdownMenu.Trigger>
                  <IconButton>
                    <MoreHorizontal />
                  </IconButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <DropdownMenu.Item onSelect={() => setIsEditProtocolDialogOpen(true)}>
                    Edit
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={() => setIsManageClinicsDialogOpen(true)}>
                    Manage Visibility
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={() => setIsDeleteProtocolDialogOpen(true)}>
                    Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.DropdownMenu>
            )}
          </div>
          <div className={"pt-3"}>
            <p className={"text-sm leading-tight text-gray-600"}>{protocol.description}</p>
            {canManage && (
              <div className={"flex items-center gap-2 mt-2"}>
                {protocol.visibleToClinics.length > 0 ? (
                  <Eye size={14} className="text-gray-500" />
                ) : (
                  <EyeOff size={14} className="text-gray-500" />
                )}
                <div className={"flex flex-wrap gap-1"}>
                  {protocol.visibleToClinics.length > 0 ? (
                    protocol.visibleToClinics.map((clinic) => (
                      <Badge key={clinic.id}>{clinic.name}</Badge>
                    ))
                  ) : (
                    <Badge className="bg-yellow-100 border-yellow-200 text-yellow-800">
                      Not visible to any clinics
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </a>
      </Link>

      {canManage && (
        <EditProtocolDialog
          protocol={protocol}
          open={isEditProtocolDialogOpen}
          onOpenChange={setIsEditProtocolDialogOpen}
        />
      )}

      {canManage && (
        <DeleteProtocolDialog
          protocol={protocol}
          open={isDeleteProtocolDialogOpen}
          onOpenChange={setIsDeleteProtocolDialogOpen}
        />
      )}

      {canManage && (
        <ManageClinicsDialog
          item={protocol}
          itemType="protocol"
          open={isManageClinicsDialogOpen}
          onOpenChange={setIsManageClinicsDialogOpen}
          onSave={handleSaveProtocolVisibility}
        />
      )}
    </>
  );
};
